// E8CompositeTrapezoidPerimeterClosedTask.js
// Zadanie zamkniete E8: obwod trapezu zlozonego z kwadratu, trojkata i rombu.

import React from "react";
import { BlockMath, ChoiceLabel, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import SvgMathLabel from "../../../../components/diagrams/SvgMathLabel";

const LETTERS = ["A", "B", "C", "D"];

const PYTHAGOREAN_CASES = [
  { squareSide: 3, triangleTop: 4, rhombusSide: 5 },
  { squareSide: 4, triangleTop: 3, rhombusSide: 5 },
  { squareSide: 6, triangleTop: 8, rhombusSide: 10 },
  { squareSide: 8, triangleTop: 6, rhombusSide: 10 },
  { squareSide: 5, triangleTop: 12, rhombusSide: 13 },
  { squareSide: 12, triangleTop: 5, rhombusSide: 13 },
  { squareSide: 10, triangleTop: 24, rhombusSide: 26 },
  { squareSide: 24, triangleTop: 10, rhombusSide: 26 },
  { squareSide: 8, triangleTop: 15, rhombusSide: 17 },
  { squareSide: 15, triangleTop: 8, rhombusSide: 17 },
];

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function shuffle(values) {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function buildOptions(correct, distractors) {
  const values = [correct, ...distractors.filter((value) => value !== correct)];
  const unique = [...new Set(values)].slice(0, 4);
  const ordered = shuffle(unique).map((value) => ({
    value,
    label: String(value),
  }));
  return {
    options: ordered,
    correctIndex: ordered.findIndex((option) => option.value === correct),
  };
}

function buildData({ squareSide, triangleTop, rhombusSide, options = null }) {
  const perimeter = 3 * squareSide + triangleTop + 3 * rhombusSide;
  const builtOptions =
    options ||
    buildOptions(perimeter, [
      squareSide + triangleTop + rhombusSide,
      2 * squareSide + triangleTop + 2 * rhombusSide,
      4 * squareSide + triangleTop + 4 * rhombusSide,
    ]);

  return {
    squareSide,
    triangleTop,
    rhombusSide,
    perimeter,
    options: builtOptions.options,
    correctIndex: builtOptions.correctIndex,
  };
}

function createOriginalTask() {
  return {
    type: "E8CompositeTrapezoidPerimeterClosedTask",
    data: buildData({
      squareSide: 6,
      triangleTop: 8,
      rhombusSide: 10,
      options: {
        options: [56, 72, 88, 120].map((value) => ({
          value,
          label: String(value),
        })),
        correctIndex: 0,
      },
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 13,
    },
    tags: ["e8", "planimetria", "czworokaty", "trapezy", "obwod"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const { squareSide, triangleTop, rhombusSide } = pick(PYTHAGOREAN_CASES);

  return {
    type: "E8CompositeTrapezoidPerimeterClosedTask",
    data: buildData({ squareSide, triangleTop, rhombusSide }),
    source: { variant: "random", basedOn: "E8_2025_MAJ_13" },
    tags: ["e8", "planimetria", "czworokaty", "trapezy", "obwod"],
  };
}

function Diagram({ data }) {
  const line = "currentColor";
  const accent = "#f0ad3d";
  const fillSquare = "rgba(240, 173, 61, 0.26)";
  const fillTriangle = "rgba(240, 173, 61, 0.36)";
  const fillRhombus = "rgba(240, 173, 61, 0.20)";

  const s = data.squareSide;   // bok kwadratu (AE, EG, GD, DA)
  const t = data.triangleTop;  // EF (dolny bok trojkata)
  const r = data.rhombusSide;  // bok rombu (FB, BC, CG, GF)

  // Skala dobrana pod stala szerokosc rysunku (~10% wieksza niz wczesniej)
  const unit = 350 / (s + t + r);
  const padX = 48;
  const padTop = 42;
  const padBottom = 44;
  const shapeH = s * unit;
  const bottomY = padTop + shapeH;
  const topY = padTop;
  const W = (s + t + r) * unit + 2 * padX;
  const H = shapeH + padTop + padBottom;
  const X = (x) => padX + x * unit;

  // Wierzcholki (orientacja jak w arkuszu CKE: A,E,F,B na dole; D,G,C na gorze)
  const A = { x: X(0), y: bottomY };
  const E = { x: X(s), y: bottomY };
  const F = { x: X(s + t), y: bottomY };
  const B = { x: X(s + t + r), y: bottomY };
  const D = { x: X(0), y: topY };
  const G = { x: X(s), y: topY };
  const C = { x: X(s + r), y: topY };

  const pts = (arr) => arr.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  // przekatna GF (do obrotu etykiety dlugosci rombu)
  const gfMid = { x: (G.x + F.x) / 2, y: (G.y + F.y) / 2 };
  const gfAngle = (Math.atan2(F.y - G.y, F.x - G.x) * 180) / Math.PI;

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "18px 0" }}>
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Trapez ABCD podzielony na kwadrat AEGD, trojkat EFG i romb FBCG"
        style={{ maxWidth: "100%", height: "auto" }}
      >
        {/* figury skladowe */}
        <polygon points={pts([A, E, G, D])} fill={fillSquare} stroke={accent} strokeWidth="2.5" />
        <polygon points={pts([E, F, G])} fill={fillTriangle} stroke={accent} strokeWidth="2.5" />
        <polygon points={pts([F, B, C, G])} fill={fillRhombus} stroke={accent} strokeWidth="2.5" />
        {/* obrys trapezu */}
        <polygon points={pts([A, B, C, D])} fill="none" stroke={line} strokeWidth="3" strokeLinejoin="round" />

        {/* litery wierzcholkow (KaTeX) */}
        <SvgMathLabel x={A.x - 14} y={bottomY + 16} math="A" />
        <SvgMathLabel x={E.x} y={bottomY + 16} math="E" />
        <SvgMathLabel x={F.x} y={bottomY + 16} math="F" />
        <SvgMathLabel x={B.x + 14} y={bottomY + 16} math="B" />
        <SvgMathLabel x={D.x - 14} y={topY - 13} math="D" />
        <SvgMathLabel x={G.x} y={topY - 13} math="G" />
        <SvgMathLabel x={C.x} y={topY - 13} math="C" />

        {/* dlugosci bokow (KaTeX, przy bokach) */}
        {/* s: pionowy bok EG */}
        <SvgMathLabel x={E.x - 14} y={(topY + bottomY) / 2} math={`${s}`} fontSize={15} color={accent} />
        {/* t: dolny bok EF */}
        <SvgMathLabel x={(E.x + F.x) / 2} y={bottomY + 16} math={`${t}`} fontSize={15} color={accent} />
        {/* r: przekatna GF (obrocona wzdluz odcinka) */}
        <g transform={`rotate(${gfAngle.toFixed(1)} ${gfMid.x} ${gfMid.y})`}>
          <SvgMathLabel x={gfMid.x} y={gfMid.y - 13} math={`${r}`} fontSize={15} color={accent} />
        </g>
      </svg>
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Trapez <InlineMath math="ABCD" /> podzielono na trzy figury: kwadrat{" "}
        <InlineMath math="AEGD" />, trójkąt <InlineMath math="EFG" /> i romb{" "}
        <InlineMath math="FBCG" />. Na rysunku podano długości boków trójkąta{" "}
        <InlineMath math="EFG" />.
      </p>
      <Diagram data={data} />
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Obwód trapezu <InlineMath math="ABCD" /> jest równy
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.label}>
            <ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        label: LETTERS[index],
        textNode: LETTERS[index],
      }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  const letter = LETTERS[data.correctIndex];
  return (
    <span>
      <ChoiceOptionMath letter={letter} math={`${data.perimeter}`} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}`} />
    </span>
  );
}

export function renderExplanation({ data }) {
  const bottom = data.squareSide + data.rhombusSide;
  const top = data.squareSide + data.triangleTop + data.rhombusSide;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Kwadrat ma wszystkie boki równe. Skoro bok{" "}
        <InlineMath math="EG" /> ma długość <InlineMath math={`${data.squareSide}`} />,
        to:
      </p>
      <BlockMath math={`AE=AD=DG=${data.squareSide}`} />
      <p>
        <strong>Krok 2.</strong> Romb ma wszystkie boki równe. Skoro bok{" "}
        <InlineMath math="FG" /> ma długość <InlineMath math={`${data.rhombusSide}`} />,
        to:
      </p>
      <BlockMath math={`FB=BC=CG=${data.rhombusSide}`} />
      <p>
        <strong>Krok 3.</strong> Obliczamy długości podstaw trapezu.
      </p>
      <BlockMath
        math={`AB=AE+EF+FB=${data.squareSide}+${data.triangleTop}+${data.rhombusSide}=${top}`}
      />
      <BlockMath
        math={`CD=DG+GC=${data.squareSide}+${data.rhombusSide}=${bottom}`}
      />
      <p>
        <strong>Krok 4.</strong> Dodajemy długości boków zewnętrznych trapezu.
      </p>
      <BlockMath
        math={`O=AB+BC+CD+AD=${top}+${data.rhombusSide}+${bottom}+${data.squareSide}=${data.perimeter}`}
      />
      <p>
        Poprawna odpowiedź to{" "}
        <ChoiceLabel letter={LETTERS[data.correctIndex]} />
      </p>
    </div>
  );
}
